import { useAtomValue } from "jotai";
import { useCallback, useMemo, useRef } from "react";

import InstanceUtils from "../utils/InstanceUtils";
import { modulesAtom } from "../atoms/GlobalAtoms";
import Constants from "../utils/Constants";
import { Module } from "../types/TenantInterfaces";

const useModule = (
  moduleAPIName?: string,
  filterDefaults = false,
  filterSubitemDefaults = false,
  filterTags = true
) => {
  const modulesFromAtom = useAtomValue(modulesAtom);

  const modules = modulesFromAtom || (Constants.DEFAULT_MODULES as Module[]);

  const setupModule = useCallback(() => {
    if (!moduleAPIName || !Array.isArray(modules) || !modules?.length) {
      return null;
    }

    let mod = modules.find((m) => m.APIName === moduleAPIName);

    // ------------ CUSTOM DEFAULT MODULES SECTION -------------
    if (!mod) return CustomModuleSchema(moduleAPIName);
    // ------------ END OF CUSTOM DEFAULT MODULES SECTION -------------

    if (filterDefaults === true) {
      mod = InstanceUtils.removeDefaultModuleFields(mod, filterTags);
    }

    if (filterSubitemDefaults === true) {
      mod = InstanceUtils.removeDefaultSubitemFields(mod);
    }

    return mod;
  }, [
    modules,
    moduleAPIName,
    filterDefaults,
    filterSubitemDefaults,
    filterTags,
  ]);

  const CustomModuleSchema = (moduleAPIName: string) => {
    const DEFAULT_MODULES = Constants.DEFAULT_MODULES as Module[];

    if (moduleAPIName === "DeletedRecords") {
      let module = DEFAULT_MODULES.find((m) => m.APIName === "DeletedRecords");

      const singleSelectField = module?.Fields?.find(
        (f: any) => f?.APIName === "DataSource"
      );

      if (singleSelectField) {
        singleSelectField.SourcesList = modules.map((m) => ({
          Name: m.Name,
          Value: m.APIName,
        }));
      }

      return module;
    }

    if (moduleAPIName === "BrieCostTracker") {
      return DEFAULT_MODULES.find((m) => m.APIName === "BrieCostTracker");
    }
    if (moduleAPIName === "People") {
      return DEFAULT_MODULES.find((m) => m.APIName === "People");
    }
    if (moduleAPIName === "Tenants") {
      return DEFAULT_MODULES.find((m) => m.APIName === "Tenants");
    }
    if (moduleAPIName === "SharedDocuments") {
      return DEFAULT_MODULES.find((m) => m.APIName === "SharedDocuments");
    }
    if (moduleAPIName === "AIModels") {
      return DEFAULT_MODULES.find((m) => m.APIName === "AIModels");
    }
    if (moduleAPIName === Constants.SPRINTS_ISSUES_API_NAME) {
      return Constants.SPRINT_ISSUES_SCHEMA as Module;
    }
  };

  const module = useMemo(() => {
    return setupModule();
  }, [setupModule]);

  const setupModuleRef = useRef<any>();
  setupModuleRef.current = setupModule;

  const getModule = useCallback(
    (moduleAPIName: string, copyModule = false, filterDefaults = false) => {
      if (!Array.isArray(modules) || !moduleAPIName) return null;

      let foundModule = modules.find((m) => m?.APIName === moduleAPIName);

      if (foundModule && filterDefaults) {
        foundModule = InstanceUtils.removeDefaultModuleFields(foundModule);
      }

      if (copyModule) {
        return foundModule ? structuredClone(foundModule) : null;
      } else {
        return foundModule || null;
      }
    },
    [modules]
  );

  return {
    module,
    getModule,
    modulesLoading: !modules || modules?.length < 1,
  };
};

export default useModule;
